import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  SdfAlert,
  SdfBox,
  SdfBusyIndicator,
  SdfButton,
  SdfSpotIllustration,
} from '@synerg/react-components'

export const AssignedApps = (props) => {
  const { partnerDetails } = props;
  const { ooid } = partnerDetails || {};

  const [loading, setLoading] = useState(false);
  const [isError, setError] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [infoMessage, setInfoMessage] = useState(null);
  const [assignedApps, setAssignedApps] = useState([]);
  const [availableApps, setAvailableApps] = useState([]);

  const getSubscriptionsForOoid = async () => {
    try {
      setLoading(true);
      setShowToast(false);

      const { data } = await axios.get(
        `${import.meta.env.VITE_ROOT_PATH}/api/partner/${ooid}/subscriptions`
      );

      if (data && Array.isArray(data.appsData) && data.appsData.length > 0) {
        let assignedAppsList = [];
        let availableAppsList = [];

        data.appsData.forEach((app) => {
          if (app.found) {
            assignedAppsList.push(app);
          } else {
            availableAppsList.push(app);
          }
        });

        setAssignedApps(assignedAppsList);
        setAvailableApps(availableAppsList);
      }
    } catch (error) {
      setShowToast(true);
      setError(true);
      setInfoMessage(
        "Unable to get assigned and available applications. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const subscribeAppForOoid = async (item = {}, index) => {
    setLoading(true);
    setShowToast(false);
    try {
      await axios.post(`${import.meta.env.VITE_ROOT_PATH}/api/partner/${ooid}/subscriptions`, {
        consumerApplicationID: item.consumerAppId,
      });

      const assignedApp = [...assignedApps, item];
      setAssignedApps(assignedApp);

      const availableApp = [...availableApps];
      availableApp.splice(index, 1);
      setAvailableApps(availableApp);

      setShowToast(true);
      setError(false);
      setInfoMessage("Application subscribe successfully.");
    } catch (error) {
      setShowToast(true);
      setError(true);
      setInfoMessage("Unable to subscribe app, Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const unSubscribeAppForOoid = async (item, index) => {
    setLoading(true);
    setShowToast(false);
    try {
      await axios.post(
        `${import.meta.env.VITE_ROOT_PATH}/api/subscriptions/${item.subscriptionId}/cancel`,
        {
          subscriptionId: item.subscriptionId,
        }
      );

      const availableApp = [...availableApps, item];
      setAvailableApps(availableApp);

      const assignedApp = [...assignedApps];
      assignedApp.splice(index, 1);
      setAssignedApps(assignedApp);

      setShowToast(true);
      setError(false);
      setInfoMessage("Application unsubscribe successfully.");
    } catch (error) {
      setShowToast(true);
      setError(true);
      setInfoMessage("Unable to unsubscribe app, Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSubscriptionsForOoid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {loading ? (
        <SdfBusyIndicator
          style={{ color: "#1a8099" }}
          size="xl"
          fullScreen={true}
          overlay={true}
        />
      ) : null}

      <SdfAlert
        className="fixed min-w-1/3"
        style={{ top: "6rem", right: "calc(100% - 66%)", zIndex: "9999" }}
        hidden={!showToast}
        autoClose={true}
        autoCloseAfter={3000}
        closeable={true}
        useAnimation={true}
        status={isError ? "error" : "success"}
      >
        {infoMessage}
      </SdfAlert>

      <div className="text-heading-05 font-bold mt-10">Assign apps</div>
      <div className="w-full">
        <div className="text-body-02 mt-2">
          Add and manage applications within the ADP Marketplace ecosystem for
          Marketplace partners.
        </div>
        <div className="w-full">
          <div className="text-heading-04 font-bold mt-8 mb-4">
            Assigned apps ({assignedApps ? assignedApps.length : 0})
          </div>

          {assignedApps.length === 0 ? (
            <div className="w-full text-center">
              <SdfSpotIllustration
                className="block mt-6 mb-4"
                illustrationName="empty-skills"
              />
              <div className="font-bold text-md color-grey">
                You have no assigned apps yet.
              </div>
              <div className="text-sm color-grey mt-2 mb-4">
                Select subscribe from the available apps section to assign it to
                this partner.
              </div>
            </div>
          ) : null}
          <div className="w-full flex flex-wrap gap-5">
            {assignedApps &&
              assignedApps.map((item, idx) => {
                return (
                  <div
                    className="w-full md:w-[calc(50%-10px)]"
                    key={`assigned-app-${idx}`}
                  >
                    <SdfBox variant="shadowed" spacing="loose">
                      <div className="flex w-full items-center">
                        <div className="w-2/12 flex shrink-0">
                          <img
                            alt="contact"
                            src={`data:${item.iconMimeType};charset=utf-8;base64,${item.icon}`}
                          />
                        </div>
                        <div className="w-7/12 min-w-0 break-all">
                          <div className="px-4 text-heading-03 font-bold">
                            {item.appName}
                          </div>
                          <div className="px-4">{item.url}</div>
                        </div>
                        <div className="w-3/12 text-center">
                          <SdfButton
                            variant={"text"}
                            onClick={() => {
                              unSubscribeAppForOoid(item, idx);
                            }}
                          >
                            Unsubscribe
                          </SdfButton>
                        </div>
                      </div>
                    </SdfBox>
                  </div>
                );
              })}
          </div>
        </div>

        <div className="w-full">
          <div className="text-heading-04 font-bold mt-6 mb-4">
            Available apps ({availableApps ? availableApps.length : 0})
          </div>

          <div className="w-full flex flex-wrap gap-5">
            {availableApps &&
              availableApps.map((item, idx) => {
                return (
                  <div
                    className="w-full md:w-[calc(50%-10px)]"
                    key={`available-app-${idx}`}
                  >
                    <SdfBox
                      key={`available-app-${idx}`}
                      variant="shadowed"
                      spacing="loose"
                    >
                      <div className="flex w-full items-center">
                        <div className="w-2/12 flex shrink-0">
                          <img
                            alt="contact"
                            src={`data:${item.iconMimeType};charset=utf-8;base64,${item.icon}`}
                          />
                        </div>
                        <div className="w-7/12 min-w-0 break-all">
                          <div className="px-4 text-heading-03 font-bold">
                            {item.appName}
                          </div>
                          <div className="px-4">{item.url}</div>
                        </div>
                        <div className="w-3/12 text-center">
                          <SdfButton
                            variant={"text"}
                            onClick={() => {
                              subscribeAppForOoid(item, idx);
                            }}
                          >
                            Subscribe
                          </SdfButton>
                        </div>
                      </div>
                    </SdfBox>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </>
  );
};
